export const WORKFLOW_TYPES = {
    UNDERWRITER: "underwriter",
    COLLATERAL_UNDERWRITER: "collateralUnderwriter",
    ACCOUNT_MANAGER: "accountManager",
    UW_MANAGER: "uwManager"
};

export const LOCK_VALIDATION = {
    PASS: "Pass",
    NEEDS_VALIDATION: "Needs Validation"
};

export const PTD_STATUS = {
    NO_OPEN_PTD: "No Open PTD",
    OPEN_PTD_EXISTS: "Open PTD exists"
};

export const DOC_TYPE = {
    CONDITIONAL: "conditional",
    FINAL: "final"
};

export const EMAIL_TYPE = {
    INITIAL: "initial",
    CONDITIONAL: "conditional",
    FINAL: "final"
};

export const MILESTONES = {
    SUBMITTED_TO_UW: "Submitted to UW",
    INITIAL_APPROVAL: "Initial Approval",
    SUBMIT_FOR_CONDITION_REVIEW: "Submit for Condition Review",
    CONDITIONAL_APPROVAL: "Conditional Approval",
    SUBMIT_FOR_FINAL_REVIEW: "Submit for Final Review",
    CTC: "CTC",
    READY_FOR_DOCS: "Ready for Docs",
    DOCS_ORDERED: "Docs Ordered",
    DOCS_OUT: "Docs Out",
    DOCS_BACK: "Docs Back"
};

export const POST_CTC_MILESTONES = [MILESTONES.READY_FOR_DOCS, MILESTONES.DOCS_ORDERED, MILESTONES.DOCS_OUT, MILESTONES.DOCS_BACK];

export const UW_MANAGER_RETURN_MILESTONES = [MILESTONES.CTC, ...POST_CTC_MILESTONES];

export const milestoneAliases = {
    "Submit for Condition Approval": MILESTONES.SUBMIT_FOR_CONDITION_REVIEW
};

export const CONDITION_TITLE_LOCK_VALIDATION = "Lock Validation Required";

const MILESTONE_SUBMITTED_TO_UW = "Submitted to UW";
const MILESTONE_INITIAL_APPROVAL = "Initial Approval";
export const MILESTONE_SUBMIT_FOR_CONDITION_REVIEW = "Submit for Condition Review";
export const MILESTONE_CONDITIONAL_APPROVAL = "Conditional Approval";
export const MILESTONE_SUBMIT_FOR_FINAL_REVIEW = "Submit for Final Review";
const MILESTONE_CTC = "CTC";

export const MILESTONE_PROGRESS_ORDER = [
    MILESTONE_SUBMITTED_TO_UW,
    MILESTONE_INITIAL_APPROVAL,
    MILESTONE_SUBMIT_FOR_CONDITION_REVIEW,
    MILESTONE_CONDITIONAL_APPROVAL,
    MILESTONE_SUBMIT_FOR_FINAL_REVIEW,
    MILESTONE_CTC
];

export function isConditionClearedByTracking(condition) {
    const tracking = Array.isArray(condition?.tracking) ? condition.tracking : [];

    if (tracking.length === 0) {
        return false;
    }

    return tracking.some(trackingItem => {
        const trackingStatus = (trackingItem?.status || "").trim().toLowerCase();

        return trackingStatus === "waived" || trackingStatus === "cleared";
    });
}

export function getActivePTDs(conditions) {
    return (conditions || []).filter(
        condition => !condition?.isRemoved && (condition?.priorTo || "").trim() === "Docs" && !isConditionClearedByTracking(condition)
    );
}

export function getLockValidationCondition(conditions) {
    return (conditions || []).find(
        condition =>
            !condition?.isRemoved && (condition?.priorTo || "").trim() === "Docs" && (condition?.title || "").trim().toLowerCase() === CONDITION_TITLE_LOCK_VALIDATION.toLowerCase()
    );
}

export function findMilestoneByName(milestones, name) {
    const normalizedName = normalizeMilestoneName(name).toLowerCase();

    return (milestones || []).find(milestone => normalizeMilestoneName(milestone?.name).toLowerCase() === normalizedName);
}

function getTimestamp(value) {
    if (!value) {
        return 0;
    }

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function getApprovalAttachmentType(attachment) {
    const attachmentText = [
        attachment?.title,
        attachment?.document,
        attachment?.assignedToEntityName,
        attachment?.assignedToEntityType,
        attachment?.assignedTo?.entityName,
        attachment?.assignedTo?.entityType
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (attachmentText.includes("conditional") || attachmentText.includes("initial approval")) {
        return "conditional";
    }

    if (attachmentText.includes("final") || attachmentText.includes("clear to close") || attachmentText.includes("ctc")) {
        return "final";
    }

    return "approval";
}

export function sanitizeFileName(value) {
    return (value || "Approval Letter")
        .replace(/[<>:"/\\|?*]+/g, "-")
        .replace(/\s+/g, " ")
        .trim();
}

export function getCreatedDateDisplay(attachment) {
    if (attachment?.createdDatePacific) {
        return attachment.createdDatePacific;
    }

    const rawDate = attachment?.createdDateUtc || attachment?.createdDate;

    if (!rawDate) {
        return "-";
    }

    const parsedDate = new Date(rawDate);

    if (Number.isNaN(parsedDate.getTime())) {
        return rawDate;
    }

    return parsedDate.toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export function getCurrentVersionState(value) {
    if (
        value === true ||
        value === 1 ||
        ["true", "1", "y", "yes", "on", "active"].includes(
            String(value || "")
                .trim()
                .toLowerCase()
        )
    ) {
        return true;
    }

    if (
        value === false ||
        value === 0 ||
        ["false", "0", "n", "no", "off", "inactive"].includes(
            String(value || "")
                .trim()
                .toLowerCase()
        )
    ) {
        return false;
    }

    return null;
}

export function getApprovalCreatedTimestamp(attachment) {
    return getTimestamp(attachment?.createdDateUtc || attachment?.createdDate || attachment?.createdDatePacific);
}

export function normalizeMilestoneName(milestoneName) {
    const value = (milestoneName || "").trim();
    return milestoneAliases[value] || value;
}

export function getLockValidationStatus(rateLockStatus) {
    return (rateLockStatus || "").trim() === LOCK_VALIDATION.NEEDS_VALIDATION ? LOCK_VALIDATION.NEEDS_VALIDATION : LOCK_VALIDATION.PASS;
}

export function getOpenPTDStatus(hasOpenPTD) {
    return hasOpenPTD ? PTD_STATUS.OPEN_PTD_EXISTS : PTD_STATUS.NO_OPEN_PTD;
}

export function findMatchingLoanApprovalRule({ loanApprovalRules, workflowType, lockValidation, openPTDCondition }) {
    const rules = Array.isArray(loanApprovalRules) ? loanApprovalRules : [];

    return rules.find(
        currentRule =>
            currentRule.workflowType === workflowType &&
            (!currentRule.lockValidation || currentRule.lockValidation === lockValidation) &&
            (!currentRule.openPTDCondition || currentRule.openPTDCondition === openPTDCondition)
    );
}

export function getLoanApprovalDecision({ currentMilestone, workflowType, rateLockStatus, hasOpenPTD, loanApprovalRules }) {
    const normalizedMilestone = normalizeMilestoneName(currentMilestone);

    const lockValidation = getLockValidationStatus(rateLockStatus);

    const openPTDCondition = getOpenPTDStatus(hasOpenPTD);

    const rule = findMatchingLoanApprovalRule({ loanApprovalRules, workflowType, lockValidation, openPTDCondition });

    if (!rule) {
        const workflowDisplay = workflowType || "-";

        return {
            isSupported: false,
            disablePreview: true,
            disableApprove: true,
            currentMilestone: normalizedMilestone,
            workflowType: workflowType || null,
            lockValidation,
            openPTDCondition,
            docType: null,
            notificationType: null,
            notifyBroker: false,
            notifyBrokerWithAttachment: false,
            targetMilestone: null,
            milestoneAction: null,
            ensureLockValidationCondition: false,
            generateSupportingDocuments: false,
            preview: "-",
            actionSummary: [`Loan Approval is not supported for current milestone "${normalizedMilestone || "-"}" and workflow type "${workflowDisplay}".`],
            message: `Loan Approval is not supported for current milestone "${normalizedMilestone || "-"}" and workflow type "${workflowDisplay}".`
        };
    }

    return {
        ...rule,
        isSupported: true,
        disablePreview: !rule.docType,
        disableApprove: false,
        currentMilestone: normalizedMilestone,
        message: ""
    };
}

export function parseValidationMessage(details) {
    const prefix = "Advanced Code Error ";

    if (typeof details !== "string" || !details.startsWith(prefix)) {
        return { type: "plain", text: details };
    }

    let rest = details.slice(prefix.length);

    const milestoneMatch = rest.match(/,\s*for\s*'([^']+)'\.?\s*$/);

    if (!milestoneMatch) {
        return { type: "plain", text: details };
    }

    const milestoneName = milestoneMatch[1];

    rest = rest.slice(0, milestoneMatch.index);

    if (rest.startsWith("'")) rest = rest.slice(1);
    if (rest.endsWith("'")) rest = rest.slice(0, -1);

    const messages = rest
        .split("', '")
        .map(m => m.trim())
        .filter(Boolean);

    if (messages.length === 0) {
        return { type: "plain", text: details };
    }

    return { type: "grouped", milestoneName, messages };
}

export function groupValidationMessages(validationMessages) {
    const plainMessages = [];
    const groupedMap = new Map();
    const order = [];

    validationMessages.forEach(message => {
        const parsed = parseValidationMessage(message);

        if (parsed.type === "plain") {
            plainMessages.push(parsed.text);
            return;
        }

        if (!groupedMap.has(parsed.milestoneName)) {
            groupedMap.set(parsed.milestoneName, []);
            order.push(parsed.milestoneName);
        }

        groupedMap.get(parsed.milestoneName).push(...parsed.messages);
    });

    return {
        plainMessages,
        groups: order.map(milestoneName => ({
            milestoneName,
            messages: groupedMap.get(milestoneName)
        }))
    };
}
